import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const COP = v => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(v);

export default function Admin() {
  const { user, logout, API, suscripcion, cargarSuscripcion } = useAuth();
  const nav = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [tab, setTab] = useState('usuarios');

  // Formulario para agregar usuario
  const [nuevoUser, setNuevoUser] = useState({ nombre: '', email: '', password: '', cedula: '', celular: '', cargo: '' });
  const [addMsg, setAddMsg] = useState('');
  const [addErr, setAddErr] = useState('');

  useEffect(() => {
    if (user?.rol !== 'admin') { nav('/'); return; }
    cargar();
  }, []);

  const cargar = async () => {
    const [u, h] = await Promise.all([
      axios.get(`${API}/admin/usuarios`),
      axios.get(`${API}/historial`)
    ]);
    setUsuarios(u.data);
    setHistorial(h.data);
    cargarSuscripcion();
  };

  const aprobar = async (id) => {
    await axios.put(`${API}/admin/usuarios/${id}/aprobar`);
    cargar();
  };

  const desactivar = async (id) => {
    await axios.put(`${API}/admin/usuarios/${id}/desactivar`);
    cargar();
  };

  const cambiarRol = async (id, rol) => {
    const fd = new FormData(); fd.append('rol', rol);
    await axios.put(`${API}/admin/usuarios/${id}/rol`, fd);
    cargar();
  };

  const agregarUsuario = async (e) => {
    e.preventDefault();
    setAddMsg(''); setAddErr('');
    try {
      await axios.post(`${API}/registro`, nuevoUser);
      setAddMsg('Usuario agregado. Apruébalo en la lista de abajo.');
      setNuevoUser({ nombre: '', email: '', password: '', cedula: '', celular: '', cargo: '' });
      cargar();
    } catch (err) {
      setAddErr(err.response?.data?.detail || 'Error al agregar usuario');
    }
  };

  const estadoColor = { activa: '#16a34a', trial: '#2563eb', vencida: '#dc2626', cancelada: '#6b7280', pendiente: '#d97706' };
  const estadoLabel = { activa: 'Activa', trial: 'Prueba gratuita', vencida: 'Vencida', cancelada: 'Cancelada', pendiente: 'Pago en proceso' };
  const susEstado = suscripcion?.estado;

  return (
    <div style={s.page}>
      <nav style={s.nav}>
        <span style={s.brand}>ConciliaApp — Admin</span>
        <div style={s.navRight}>
          <a href="/" style={s.link}>← Volver</a>
          <button onClick={logout} style={s.btn}>Salir</button>
        </div>
      </nav>

      <div style={s.content}>
        <div style={s.tabs}>
          {[
            ['usuarios', '👥 Usuarios'],
            ['suscripcion', '💳 Suscripción'],
            ['historial', '📋 Historial'],
          ].map(([k, lbl]) => (
            <button key={k} style={{ ...s.tab, ...(tab === k ? s.tabActive : {}) }} onClick={() => setTab(k)}>
              {lbl}
            </button>
          ))}
        </div>

        {/* ── USUARIOS ── */}
        {tab === 'usuarios' && (
          <div style={s.card}>
            <h2 style={s.title}>Gestión de usuarios</h2>

            {/* Formulario agregar usuario */}
            <details style={s.details}>
              <summary style={s.summary}>+ Agregar nuevo usuario</summary>
              <form onSubmit={agregarUsuario} style={s.addForm}>
                <div style={s.row2}>
                  <input style={s.input} placeholder="Nombre *" value={nuevoUser.nombre}
                    onChange={e => setNuevoUser({ ...nuevoUser, nombre: e.target.value })} required />
                  <input style={s.input} type="email" placeholder="Correo *" value={nuevoUser.email}
                    onChange={e => setNuevoUser({ ...nuevoUser, email: e.target.value })} required />
                </div>
                <div style={s.row2}>
                  <input style={s.input} type="password" placeholder="Contraseña *" value={nuevoUser.password}
                    onChange={e => setNuevoUser({ ...nuevoUser, password: e.target.value })} required />
                  <input style={s.input} placeholder="Cédula" value={nuevoUser.cedula}
                    onChange={e => setNuevoUser({ ...nuevoUser, cedula: e.target.value })} />
                </div>
                <div style={s.row2}>
                  <input style={s.input} placeholder="Celular" value={nuevoUser.celular}
                    onChange={e => setNuevoUser({ ...nuevoUser, celular: e.target.value })} />
                  <input style={s.input} placeholder="Cargo" value={nuevoUser.cargo}
                    onChange={e => setNuevoUser({ ...nuevoUser, cargo: e.target.value })} />
                </div>
                {addErr && <p style={{ color: '#dc2626', fontSize: 12, margin: 0 }}>{addErr}</p>}
                {addMsg && <p style={{ color: '#16a34a', fontSize: 12, margin: 0 }}>{addMsg}</p>}
                <button type="submit" style={s.btnOk}>Agregar usuario</button>
              </form>
            </details>

            <table style={s.table}>
              <thead>
                <tr>{['Nombre', 'Correo', 'Rol', 'Estado', 'Registro', 'Acciones'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {usuarios.map(u => (
                  <tr key={u.id}>
                    <td style={s.td}>{u.nombre}</td>
                    <td style={s.td}>{u.email}</td>
                    <td style={s.td}>
                      <select style={s.rolSelect} value={u.rol}
                        onChange={e => cambiarRol(u.id, e.target.value)}
                        disabled={u.rol === 'admin' && u.id === user?.id}>
                        <option value="admin">admin</option>
                        <option value="usuario">usuario</option>
                        <option value="digitador">digitador</option>
                        <option value="auditor">auditor</option>
                      </select>
                    </td>
                    <td style={s.td}>
                      <span style={u.activo ? s.badgeOk : s.badgePending}>
                        {u.activo ? 'Activo' : 'Pendiente'}
                      </span>
                    </td>
                    <td style={s.td}>{new Date(u.created_at).toLocaleDateString('es-CO')}</td>
                    <td style={s.td}>
                      {!u.activo && <button style={s.btnOk} onClick={() => aprobar(u.id)}>Aprobar</button>}
                      {u.activo && u.rol !== 'admin' && <button style={s.btnDel} onClick={() => desactivar(u.id)}>Desactivar</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── SUSCRIPCIÓN ── */}
        {tab === 'suscripcion' && (
          <div style={s.card}>
            <h2 style={s.title}>Suscripción</h2>

            {suscripcion ? (
              <>
                <div style={s.susRow}>
                  <div style={{ ...s.susBadge, background: (estadoColor[susEstado] || '#888') + '20', color: estadoColor[susEstado] || '#888' }}>
                    {estadoLabel[susEstado] || susEstado}
                  </div>
                  {suscripcion.dias_restantes !== null && (
                    <span style={s.susDias}>
                      {suscripcion.dias_restantes > 0
                        ? `${suscripcion.dias_restantes} días restantes`
                        : 'Vencida'}
                    </span>
                  )}
                </div>

                <div style={s.susGrid}>
                  <div style={s.susItem}>
                    <span style={s.susLabel}>Plan</span>
                    <span style={s.susVal}>{suscripcion.plan_nombre || '—'}</span>
                  </div>
                  <div style={s.susItem}>
                    <span style={s.susLabel}>Tipo</span>
                    <span style={s.susVal}>{suscripcion.tipo === 'trial' ? 'Prueba gratuita' : suscripcion.tipo}</span>
                  </div>
                  <div style={s.susItem}>
                    <span style={s.susLabel}>Precio mensual</span>
                    <span style={s.susVal}>{suscripcion.precio_mensual ? COP(suscripcion.precio_mensual) : '—'}</span>
                  </div>
                  <div style={s.susItem}>
                    <span style={s.susLabel}>Precio anual</span>
                    <span style={s.susVal}>{suscripcion.precio_anual ? COP(suscripcion.precio_anual) : '—'}</span>
                  </div>
                  {suscripcion.fecha_fin && (
                    <div style={s.susItem}>
                      <span style={s.susLabel}>Válida hasta</span>
                      <span style={s.susVal}>{new Date(suscripcion.fecha_fin).toLocaleDateString('es-CO')}</span>
                    </div>
                  )}
                  <div style={s.susItem}>
                    <span style={s.susLabel}>Max. usuarios</span>
                    <span style={s.susVal}>{suscripcion.max_usuarios}</span>
                  </div>
                </div>

                <button style={s.btnPagar} onClick={() => nav('/suscripcion')}>
                  {['vencida', 'cancelada'].includes(susEstado) ? 'Renovar suscripción' : 'Gestionar suscripción'}
                </button>
              </>
            ) : (
              <div>
                <p style={{ color: '#dc2626', marginBottom: 16 }}>No tienes suscripción activa.</p>
                <button style={s.btnPagar} onClick={() => nav('/suscripcion')}>Ver planes y suscribirse</button>
              </div>
            )}
          </div>
        )}

        {/* ── HISTORIAL ── */}
        {tab === 'historial' && (
          <div style={s.card}>
            <h2 style={s.title}>Historial de conciliaciones</h2>
            <table style={s.table}>
              <thead>
                <tr>{['Nombre', 'Usuario', 'Banco', 'Auxiliar', 'Cruzan', 'No cruzan', 'Fecha'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {historial.map(h => (
                  <tr key={h.id}>
                    <td style={s.td}>{h.nombre}</td>
                    <td style={s.td}>{h.usuario}</td>
                    <td style={s.td}>{h.total_banco}</td>
                    <td style={s.td}>{h.total_aux}</td>
                    <td style={{ ...s.td, color: '#16a34a', fontWeight: 600 }}>{h.cruzan}</td>
                    <td style={{ ...s.td, color: '#dc2626', fontWeight: 600 }}>{h.no_cruzan_banco + h.no_cruzan_aux}</td>
                    <td style={s.td}>{new Date(h.created_at).toLocaleString('es-CO')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui,sans-serif' },
  nav: { background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  brand: { fontWeight: 700, fontSize: 16 },
  navRight: { display: 'flex', alignItems: 'center', gap: 12 },
  link: { fontSize: 13, color: '#2563eb', textDecoration: 'none' },
  btn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: 'transparent', cursor: 'pointer', fontSize: 13 },
  content: { padding: '24px', maxWidth: 1200, margin: '0 auto' },
  tabs: { display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #e5e7eb' },
  tab: { padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, color: '#666', borderBottom: '2px solid transparent', marginBottom: -1 },
  tabActive: { color: '#111', borderBottomColor: '#2563eb', fontWeight: 600 },
  card: { background: '#fff', borderRadius: 14, padding: '24px', border: '1px solid #e5e7eb' },
  title: { fontSize: 16, fontWeight: 600, margin: '0 0 16px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { background: '#f8fafc', padding: '10px 14px', textAlign: 'left', fontWeight: 500, color: '#555', borderBottom: '1px solid #e5e7eb' },
  td: { padding: '10px 14px', borderBottom: '1px solid #f0f0f0' },
  rolSelect: { padding: '4px 8px', borderRadius: 7, border: '1px solid #e5e7eb', fontSize: 12, background: '#f8fafc', cursor: 'pointer' },
  badgeOk: { background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 },
  badgePending: { background: '#fff7ed', color: '#c2410c', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 },
  btnOk: { padding: '4px 12px', borderRadius: 6, background: '#16a34a', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, marginRight: 6 },
  btnDel: { padding: '4px 12px', borderRadius: 6, background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12 },
  // Agregar usuario
  details: { border: '1px dashed #cbd5e1', borderRadius: 10, padding: '12px 16px', marginBottom: 20 },
  summary: { cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#2563eb' },
  addForm: { display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  input: { padding: '8px 12px', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 13 },
  // Suscripción tab
  susRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 },
  susBadge: { padding: '4px 14px', borderRadius: 20, fontWeight: 700, fontSize: 13 },
  susDias: { fontSize: 13, color: '#6b7280' },
  susGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 },
  susItem: { background: '#f8fafc', borderRadius: 10, padding: '12px 14px' },
  susLabel: { display: 'block', fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 },
  susVal: { fontSize: 15, fontWeight: 600, color: '#111' },
  btnPagar: { padding: '10px 24px', borderRadius: 8, background: '#2563eb', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
};
